import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import type { TourStop } from '../../types/deliveryTour';

const STATUS: Record<TourStop['status'], { ring: string; label: string }> = {
  pending: { ring: 'bg-brand-600 text-white', label: 'Pending' },
  delivered: { ring: 'bg-green-600 text-white', label: 'Delivered' },
  skipped: { ring: 'bg-gray-400 text-white', label: 'Skipped' },
};

interface StopCardProps {
  stop: TourStop;
  sortable: boolean;
  onOpen: (stop: TourStop) => void;
}

export const StopCard: React.FC<StopCardProps> = ({
  stop,
  sortable,
  onOpen,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stop.id, disabled: !sortable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const status = STATUS[stop.status];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-2 bg-white rounded-2xl border border-gray-100 px-3 py-2.5 ${
        isDragging ? 'opacity-70 shadow-lg' : ''
      }`}
    >
      {sortable ? (
        <button
          type="button"
          className="flex-shrink-0 p-1.5 text-gray-400 touch-none"
          aria-label="Reorder stop"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={18} />
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onOpen(stop)}
        className="flex-1 min-w-0 text-left flex gap-3 items-center"
      >
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black leading-none tabular-nums flex-shrink-0 ${status.ring}`}
        >
          {stop.sequence_order}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-black text-gray-900 truncate leading-tight">
            {stop.customer_name}
          </p>
          <p className="text-xs text-gray-500 truncate">{stop.customer_address}</p>
          <p className="text-[11px] font-bold text-gray-400 mt-0.5">
            {status.label}
          </p>
        </div>
      </button>
    </div>
  );
};
